import { useState, useMemo, useRef, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api, apiPost, apiPut, apiDelete, apiUpload, imageUrl } from '../lib/api'
import { RoadmapGallery } from '../components/RoadmapGallery'
import { Logo } from '../components/Logo'
import '../styles/roadmap.css'
import '../styles/public-roadmap.css'

// ── Types ──────────────────────────────────────────────────────────────
interface Phase {
    id: string; name: string; description?: string; color?: string; icon?: string
    sortOrder: number; isDefault: boolean; isTerminal: boolean
}
interface Category { id: string; name: string; sortOrder: number }
interface Product {
    id: string; name: string; description?: string; color?: string; icon?: string
    sortOrder: number; categories: Category[]
}
interface ImageRef { url: string; width?: number; height?: number }
interface UploadedImg { id: string; url: string; width: number; height: number }
interface RequestRow {
    id: string; title: string; description: string; problem?: string; plannedSolution?: string
    productId?: string; categoryId?: string; phaseId?: string
    targetRelease?: string; imageUrl?: string; isHeadOfficeOnly?: boolean; images?: ImageRef[]
    voteCount: number; voteScore: number; commentCount: number
    submitter?: string; createdAt: string; publishedAt?: string
    myVote?: number | null
}
interface BoardData { products: Product[]; phases: Phase[]; requests: RequestRow[] }
interface RejectedRow {
    id: string; title: string; description: string; problem?: string
    productId?: string; categoryId?: string
    declineReason?: string; isHeadOfficeOnly?: boolean
    voteCount: number; voteScore: number
    submitter?: string; createdAt: string; updatedAt?: string
}
interface Comment {
    id: string; body: string; author?: string; isOfficial: boolean; createdAt: string
    images?: ImageRef[]
}
interface RequestDetail extends RequestRow {
    status: string; declineReason?: string; submitterEmail?: string
    externalUrl?: string; updatedAt?: string; comments: Comment[]
}

// Extracts image files from a paste/drop event's data.
function imageFilesFrom(items: DataTransferItemList | null, files: FileList | null): File[] {
    const out: File[] = []
    if (items) for (const it of Array.from(items)) {
        if (it.kind === 'file' && it.type.startsWith('image/')) { const f = it.getAsFile(); if (f) out.push(f) }
    }
    if (out.length === 0 && files) for (const f of Array.from(files)) {
        if (f.type.startsWith('image/')) out.push(f)
    }
    return out
}

// Manages a list of uploaded images for a form (idea or comment).
function useImageUpload(token: string | null) {
    const [images, setImages] = useState<UploadedImg[]>([])
    const [uploading, setUploading] = useState(0)
    const [error, setError] = useState<string | null>(null)

    const uploadFiles = async (files: File[]) => {
        if (!files.length) return
        setError(null)
        setUploading(u => u + files.length)
        const done: UploadedImg[] = []
        for (const f of files) {
            try {
                const res: any = await apiUpload('/api/roadmap/uploads', token, f)
                done.push({ id: res.id, url: res.url, width: res.width, height: res.height })
            } catch (e: any) {
                let msg = 'Upload failed.'
                try { msg = JSON.parse(e?.message)?.error ?? msg } catch { /* keep default */ }
                setError(msg)
            } finally {
                setUploading(u => u - 1)
            }
        }
        if (done.length) setImages(prev => [...prev, ...done])
    }

    const remove = (id: string) => setImages(prev => prev.filter(i => i.id !== id))
    return { images, setImages, uploadFiles, remove, uploading, error }
}

// Editable thumbnails of just-uploaded images, with remove buttons.
function UploadThumbs({ images, onRemove, uploading }: {
    images: UploadedImg[]; onRemove: (id: string) => void; uploading: number
}) {
    if (images.length === 0 && uploading === 0) return null
    return (
        <div className="roadmap-upload-thumbs">
            {images.map(img => (
                <div key={img.id} className="roadmap-upload-thumb">
                    <img src={imageUrl(img.url)} alt="" />
                    <button type="button" className="roadmap-upload-remove" title="Remove" onClick={() => onRemove(img.id)}>✕</button>
                </div>
            ))}
            {uploading > 0 && <div className="roadmap-upload-thumb roadmap-upload-pending"><div className="spinner" /></div>}
        </div>
    )
}

const IMPORTANCE = [
    { value: 'NotImportant', label: 'Not important', weight: 1 },
    { value: 'NiceToHave', label: 'Nice-to-have', weight: 2 },
    { value: 'Important', label: 'Important', weight: 3 },
    { value: 'Critical', label: 'Critical', weight: 4 },
] as const

const weightToLabel = (w?: number | null) =>
    IMPORTANCE.find(i => i.weight === w)?.label ?? null

const fmtDate = (s?: string) =>
    s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''

// ════════════════════════════════════════════════════════════════════════
export default function PublicRoadmapPage() {
    const token = useAuthStore(s => s.accessToken)
    const user = useAuthStore(s => s.user)
    const qc = useQueryClient()

    const [searchParams, setSearchParams] = useSearchParams()
    const [productFilter, setProductFilter] = useState<string>('all')
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState<'top' | 'recent'>('top')
    const [submitOpen, setSubmitOpen] = useState(false)
    const [detailId, setDetailId] = useState<string | null>(null)
    const [votePopover, setVotePopover] = useState<string | null>(null)
    const [toast, setToast] = useState<string | null>(null)

    const showToast = (msg: string) => {
        setToast(msg)
        setTimeout(() => setToast(null), 4000)
    }

    const { data, isLoading } = useQuery<BoardData>({
        queryKey: ['public-roadmap-board', token],
        queryFn: () => api('/api/roadmap/board', token),
    })

    // Declined ideas — the "Rejected" bucket. Signed-in only (endpoint 401s for anonymous),
    // so we only fetch when there's a token.
    const { data: rejectedData, isLoading: rejectedLoading } = useQuery<{ requests: RejectedRow[] }>({
        queryKey: ['public-roadmap-rejected', token],
        queryFn: () => api('/api/roadmap/rejected', token),
        enabled: !!token,
    })

    const phases = useMemo(() => (data?.phases ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder), [data])
    const products = data?.products ?? []
    const requests = data?.requests ?? []

    // Active phase lives in the URL (?phase=<id>) so it survives a refresh / is shareable.
    // Falls back to the first phase when the param is missing or no longer valid.
    const phaseParam = searchParams.get('phase')
    // "rejected" is a virtual bucket (declined items), addressable via ?phase=rejected but
    // only honoured for signed-in users — anonymous visitors fall back to the first phase.
    const rejectedView = phaseParam === 'rejected' && !!token
    const activePhase = rejectedView
        ? 'rejected'
        : (phaseParam && phases.some(p => p.id === phaseParam))
            ? phaseParam
            : (phases[0]?.id ?? null)
    const setActivePhase = (id: string) => {
        const next = new URLSearchParams(searchParams)
        next.set('phase', id)
        setSearchParams(next, { replace: true })
    }

    // Deep-link: ?request=<id> (e.g. from an email) opens that idea's detail directly, regardless
    // of which phase it's in.
    const requestParam = searchParams.get('request')
    useEffect(() => {
        if (requestParam) setDetailId(requestParam)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestParam])

    const closeDetail = () => {
        setDetailId(null)
        if (searchParams.has('request')) {
            const next = new URLSearchParams(searchParams)
            next.delete('request')
            setSearchParams(next, { replace: true })
        }
    }

    const productById = useMemo(() => new Map(products.map(p => [p.id, p])), [products])
    const categoryById = useMemo(() => {
        const m = new Map<string, Category>()
        products.forEach(p => p.categories.forEach(c => m.set(c.id, c)))
        return m
    }, [products])
    const phaseById = useMemo(() => new Map(phases.map(p => [p.id, p])), [phases])

    const countByPhase = useMemo(() => {
        const m = new Map<string, number>()
        requests.forEach(r => { if (r.phaseId) m.set(r.phaseId, (m.get(r.phaseId) ?? 0) + 1) })
        return m
    }, [requests])

    const voteMut = useMutation({
        mutationFn: (p: { id: string; importance: string }) =>
            apiPost(`/api/roadmap/requests/${p.id}/vote`, token, { importance: p.importance }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['public-roadmap-board'] })
            if (detailId) qc.invalidateQueries({ queryKey: ['public-roadmap-request', detailId] })
        },
    })
    const unvoteMut = useMutation({
        mutationFn: (id: string) => apiDelete(`/api/roadmap/requests/${id}/vote`, token),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['public-roadmap-board'] })
            if (detailId) qc.invalidateQueries({ queryKey: ['public-roadmap-request', detailId] })
        },
    })

    // SuperAdmin gets inline board management (edit in the detail modal + drag cards between phases).
    const isSuperAdmin = user?.role === 'SuperAdmin'
    const [draggingId, setDraggingId] = useState<string | null>(null)
    const [dropPhase, setDropPhase] = useState<string | null>(null)

    const movePhaseMut = useMutation({
        mutationFn: (p: { id: string; phaseId: string }) =>
            apiPut(`/api/admin/roadmap/requests/${p.id}`, token, { phaseId: p.phaseId }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['public-roadmap-board'] })
            qc.invalidateQueries({ queryKey: ['admin-roadmap-pending-count'] })
            showToast('Phase updated.')
        },
        onError: () => showToast('Could not move the idea.'),
    })

    const dropOnPhase = (phaseId: string) => {
        const id = draggingId
        setDraggingId(null); setDropPhase(null)
        if (id && phaseId !== activePhase) movePhaseMut.mutate({ id, phaseId })
    }

    const castVote = (id: string, importance: string) => {
        if (!token) { showToast('Sign in to vote on ideas.'); return }
        voteMut.mutate({ id, importance })
        setVotePopover(null)
    }

    const grouped = useMemo(() => {
        let list = requests.filter(r => r.phaseId === activePhase)
        if (productFilter !== 'all') list = list.filter(r => r.productId === productFilter)
        if (search.trim()) {
            const t = search.trim().toLowerCase()
            list = list.filter(r =>
                r.title.toLowerCase().includes(t) || r.description.toLowerCase().includes(t))
        }
        list = list.slice().sort((a, b) =>
            sort === 'recent'
                ? new Date(b.publishedAt ?? b.createdAt).getTime() - new Date(a.publishedAt ?? a.createdAt).getTime()
                : b.voteScore - a.voteScore || b.voteCount - a.voteCount)

        const byProduct = new Map<string, RequestRow[]>()
        const uncategorizedKey = '__none__'
        list.forEach(r => {
            const key = r.productId ?? uncategorizedKey
            if (!byProduct.has(key)) byProduct.set(key, [])
            byProduct.get(key)!.push(r)
        })

        const orderedProductIds = products.map(p => p.id).filter(id => byProduct.has(id))
        if (byProduct.has(uncategorizedKey)) orderedProductIds.push(uncategorizedKey)

        return orderedProductIds.map(pid => {
            const items = byProduct.get(pid)!
            const byCategory = new Map<string, RequestRow[]>()
            items.forEach(r => {
                const ck = r.categoryId ?? '__nocat__'
                if (!byCategory.has(ck)) byCategory.set(ck, [])
                byCategory.get(ck)!.push(r)
            })
            return { productId: pid, byCategory }
        })
    }, [requests, activePhase, productFilter, search, sort, products])

    const totalInPhase = grouped.reduce((n, g) => n + [...g.byCategory.values()].reduce((m, a) => m + a.length, 0), 0)

    // The rejected bucket honours the product filter + search box (but not the phase-based sort).
    const filteredRejected = useMemo(() => {
        let list = rejectedData?.requests ?? []
        if (productFilter !== 'all') list = list.filter(r => r.productId === productFilter)
        if (search.trim()) {
            const t = search.trim().toLowerCase()
            list = list.filter(r => r.title.toLowerCase().includes(t) || r.description.toLowerCase().includes(t))
        }
        return list
    }, [rejectedData, productFilter, search])

    return (
        <div className="public-roadmap-root">
            {/* ── Standalone header ── */}
            <header className="public-roadmap-header">
                <div className="public-roadmap-header-inner">
                    <div className="public-roadmap-brand">
                        <Logo height={24} />
                        <span className="public-roadmap-subtitle">Product Roadmap</span>
                    </div>
                    <div className="public-roadmap-header-actions">
                        {user ? (
                            <>
                                <span className="public-roadmap-user-badge">{user.displayName ?? user.email}</span>
                                <Link to="/dashboard" className="btn btn-secondary public-roadmap-back-btn">
                                    ← Dashboard
                                </Link>
                            </>
                        ) : (
                            <Link to="/login" className="btn btn-secondary">Sign in</Link>
                        )}
                        <button className="btn btn-primary" onClick={() => setSubmitOpen(true)}>
                            ＋ Submit idea
                        </button>
                    </div>
                </div>
            </header>

            <main className="public-roadmap-content">
                <div className="public-roadmap-hero">
                    <h1>Product Roadmap</h1>
                    <p>See what we're building, share your ideas, and vote on what matters most to you.</p>
                </div>

                {/* Phase tabs — also drop targets for SuperAdmin drag-to-move */}
                <div className="roadmap-tabs">
                    {phases.map(p => {
                        const isDropTarget = isSuperAdmin && !!draggingId
                        return (
                        <button
                            key={p.id}
                            className={`roadmap-tab${activePhase === p.id ? ' active' : ''}${dropPhase === p.id ? ' drop-target' : ''}`}
                            style={{ ['--tab-color' as any]: p.color ?? 'var(--color-primary)' }}
                            onClick={() => setActivePhase(p.id)}
                            onDragOver={isDropTarget ? (e => { e.preventDefault(); setDropPhase(p.id) }) : undefined}
                            onDragLeave={isDropTarget ? (() => setDropPhase(cur => cur === p.id ? null : cur)) : undefined}
                            onDrop={isDropTarget ? (e => { e.preventDefault(); dropOnPhase(p.id) }) : undefined}
                        >
                            {p.icon && <span>{p.icon}</span>}
                            <span>{p.name}</span>
                            <span className="tab-count">{countByPhase.get(p.id) ?? 0}</span>
                        </button>
                        )
                    })}
                    {token && (
                        <button
                            className={`roadmap-tab roadmap-tab-rejected${activePhase === 'rejected' ? ' active' : ''}`}
                            style={{ ['--tab-color' as any]: 'var(--color-error)' }}
                            onClick={() => setActivePhase('rejected')}
                            title="Declined ideas — visible to signed-in users only"
                        >
                            <span>🚫</span>
                            <span>Rejected</span>
                            <span className="tab-count">{rejectedData?.requests.length ?? 0}</span>
                        </button>
                    )}
                </div>
                {isSuperAdmin && draggingId && (
                    <div className="roadmap-drag-hint">Drop on a phase tab above to move this idea.</div>
                )}

                {/* Toolbar */}
                <div className="roadmap-toolbar">
                    <select className="form-select roadmap-search" value={productFilter}
                        onChange={e => setProductFilter(e.target.value)} style={{ maxWidth: 220 }}>
                        <option value="all">All products</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input
                        className="form-input roadmap-search"
                        placeholder="Search ideas…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <div className="spacer" />
                    <div style={{ display: 'flex', gap: 4 }}>
                        <button className={`btn ${sort === 'top' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSort('top')}>Top voted</button>
                        <button className={`btn ${sort === 'recent' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setSort('recent')}>Recent</button>
                    </div>
                </div>

                {/* Board */}
                {activePhase === 'rejected' ? (
                    <RejectedBucket requests={filteredRejected} productById={productById} isLoading={rejectedLoading} />
                ) : isLoading ? (
                    <div className="dashboard-loading"><div className="spinner" /><span>Loading roadmap…</span></div>
                ) : totalInPhase === 0 ? (
                    <div className="empty-state">
                        <div className="icon">🗳️</div>
                        <h3>Nothing here yet</h3>
                        <p>No requests in this phase{productFilter !== 'all' ? ' for this product' : ''}. Be the first to submit an idea!</p>
                    </div>
                ) : (
                    grouped.map(g => {
                        const prod = productById.get(g.productId)
                        return (
                            <div className="roadmap-group" key={g.productId}>
                                <div className="roadmap-group-header">
                                    <span className="group-dot" style={{ background: prod?.color ?? 'var(--color-text-tertiary)' }} />
                                    {prod?.icon && <span>{prod.icon}</span>}
                                    <span>{prod?.name ?? 'Uncategorized'}</span>
                                </div>
                                {[...g.byCategory.entries()].map(([catId, items]) => (
                                    <div key={catId}>
                                        {catId !== '__nocat__' && (
                                            <div className="roadmap-category-label">{categoryById.get(catId)?.name ?? 'General'}</div>
                                        )}
                                        <div className="roadmap-card-grid">
                                            {items.map(r => (
                                                <PubRequestCard
                                                    key={r.id}
                                                    req={r}
                                                    phase={r.phaseId ? phaseById.get(r.phaseId) : undefined}
                                                    product={r.productId ? productById.get(r.productId) : undefined}
                                                    isAuth={!!token}
                                                    votePopoverOpen={votePopover === r.id}
                                                    onToggleVotePopover={() => setVotePopover(votePopover === r.id ? null : r.id)}
                                                    onVote={imp => castVote(r.id, imp)}
                                                    onUnvote={() => { if (!token) return; unvoteMut.mutate(r.id); setVotePopover(null) }}
                                                    onOpen={() => setDetailId(r.id)}
                                                    draggable={isSuperAdmin}
                                                    onDragStart={() => setDraggingId(r.id)}
                                                    onDragEnd={() => { setDraggingId(null); setDropPhase(null) }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    })
                )}
            </main>

            {submitOpen && (
                <PubSubmitIdeaModal
                    products={products}
                    isAuth={!!token}
                    token={token}
                    onClose={() => setSubmitOpen(false)}
                    onSubmitted={(msg) => {
                        setSubmitOpen(false)
                        qc.invalidateQueries({ queryKey: ['public-roadmap-board'] })
                        showToast(msg)
                    }}
                />
            )}

            {detailId && (
                <PubDetailModal
                    id={detailId}
                    phaseById={phaseById}
                    productById={productById}
                    categoryById={categoryById}
                    phases={phases}
                    products={products}
                    isAuth={!!token}
                    isSuperAdmin={isSuperAdmin}
                    token={token}
                    onClose={closeDetail}
                    onSaved={() => {
                        qc.invalidateQueries({ queryKey: ['public-roadmap-board'] })
                        qc.invalidateQueries({ queryKey: ['admin-roadmap-pending-count'] })
                        if (detailId) qc.invalidateQueries({ queryKey: ['public-roadmap-request', detailId] })
                        showToast('Saved.')
                    }}
                    onVote={imp => {
                        if (!token) { showToast('Sign in to vote.'); return }
                        voteMut.mutate({ id: detailId, importance: imp })
                    }}
                    onUnvote={() => {
                        if (!token) return
                        unvoteMut.mutate(detailId)
                    }}
                />
            )}

            {toast && <div className="roadmap-toast">{toast}</div>}
        </div>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  Request card
// ════════════════════════════════════════════════════════════════════════
function PubRequestCard({ req, phase, product, isAuth, votePopoverOpen, onToggleVotePopover, onVote, onUnvote, onOpen, draggable, onDragStart, onDragEnd }: {
    req: RequestRow; phase?: Phase; product?: Product; isAuth: boolean
    votePopoverOpen: boolean; onToggleVotePopover: () => void
    onVote: (importance: string) => void; onUnvote: () => void; onOpen: () => void
    draggable?: boolean; onDragStart?: () => void; onDragEnd?: () => void
}) {
    return (
        <div
            className={`roadmap-card${votePopoverOpen ? ' popover-open' : ''}${draggable ? ' draggable' : ''}`}
            onClick={onOpen}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
        >
            {req.images && req.images.length > 0 && (
                <div className="roadmap-card-thumb">
                    <img src={imageUrl(req.images[0].url)} alt="" loading="lazy" />
                    {req.images.length > 1 && <span className="roadmap-card-thumb-count">+{req.images.length - 1}</span>}
                </div>
            )}
            <div className="roadmap-card-body">
                <div className="roadmap-card-title">
                    {req.isHeadOfficeOnly && <span className="ho-badge" title="Visible to HeadOffice / SuperAdmin only">HO</span>}
                    {req.title}
                </div>
                <div className="roadmap-card-desc">{req.problem || req.description}</div>
                <div className="roadmap-card-meta">
                    {product && (
                        <span className="roadmap-chip" style={{ background: (product.color ?? '#888') + '22', color: product.color ?? undefined }}>
                            {product.icon} {product.name}
                        </span>
                    )}
                    {phase && (
                        <span className="roadmap-chip phase" style={{ background: phase.color ?? 'var(--color-primary)' }}>
                            {phase.icon} {phase.name}
                        </span>
                    )}
                    {req.targetRelease && <span className="roadmap-chip">🎯 {req.targetRelease}</span>}
                    {req.commentCount > 0 && <span className="roadmap-card-comments">💬 {req.commentCount}</span>}
                </div>
            </div>

            <div className="vote-control" onClick={e => e.stopPropagation()}>
                <div className={`vote-box${req.myVote ? ' voted' : ''}`} onClick={onToggleVotePopover} title="Vote on this idea">
                    <span className="vote-caret">▲</span>
                    <span className="vote-score">{req.voteScore}</span>
                    <span className="vote-label">{req.myVote ? 'voted' : 'votes'}</span>
                </div>
                {votePopoverOpen && (
                    <div className="vote-popover">
                        {isAuth ? (
                            <>
                                <div className="vote-popover-title">How important is this?</div>
                                {IMPORTANCE.map(i => (
                                    <button
                                        key={i.value}
                                        className={`vote-option${req.myVote === i.weight ? ' selected' : ''}`}
                                        onClick={() => onVote(i.value)}
                                    >
                                        <span>{i.label}</span>
                                        <span className="opt-weight">+{i.weight}</span>
                                    </button>
                                ))}
                                {req.myVote ? <button className="vote-remove" onClick={onUnvote}>Remove my vote</button> : null}
                            </>
                        ) : (
                            <div className="vote-signin-prompt">
                                <Link to="/login" className="btn btn-primary" style={{ display: 'block', textAlign: 'center' }}>
                                    Sign in to vote
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  Rejected bucket (signed-in only) — read-only list of declined ideas + reasons
// ════════════════════════════════════════════════════════════════════════
function RejectedBucket({ requests, productById, isLoading }: {
    requests: RejectedRow[]; productById: Map<string, Product>; isLoading: boolean
}) {
    if (isLoading) return <div className="dashboard-loading"><div className="spinner" /><span>Loading…</span></div>
    if (requests.length === 0) {
        return (
            <div className="empty-state">
                <div className="icon">🚫</div>
                <h3>No rejected ideas</h3>
                <p>Declined ideas appear here with the reason they weren't pursued.</p>
            </div>
        )
    }
    return (
        <>
            <div className="roadmap-rejected-note">🔒 Rejected ideas are visible to signed-in users only.</div>
            <div className="roadmap-card-grid">
                {requests.map(r => {
                    const product = r.productId ? productById.get(r.productId) : undefined
                    return (
                        <div className="roadmap-card roadmap-rejected-card" key={r.id}>
                            <div className="roadmap-card-body">
                                <div className="roadmap-card-title">
                                    {r.isHeadOfficeOnly && <span className="ho-badge" title="Visible to HeadOffice / SuperAdmin only">HO</span>}
                                    {r.title}
                                </div>
                                <div className="roadmap-card-desc">{r.problem || r.description}</div>
                                {r.declineReason && (
                                    <div className="roadmap-rejected-reason"><strong>Why declined:</strong> {r.declineReason}</div>
                                )}
                                <div className="roadmap-card-meta">
                                    {product && (
                                        <span className="roadmap-chip" style={{ background: (product.color ?? '#888') + '22', color: product.color ?? undefined }}>
                                            {product.icon} {product.name}
                                        </span>
                                    )}
                                    <span className="roadmap-rejected-meta">{r.submitter ?? 'Anonymous'} · {fmtDate(r.updatedAt ?? r.createdAt)}</span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  Submit idea modal (supports both authenticated and anonymous users)
// ════════════════════════════════════════════════════════════════════════
function PubSubmitIdeaModal({ products, isAuth, token, onClose, onSubmitted }: {
    products: Product[]; isAuth: boolean; token: string | null
    onClose: () => void; onSubmitted: (msg: string) => void
}) {
    const [description, setDescription] = useState('')
    const [title, setTitle] = useState('')
    const [productId, setProductId] = useState('')
    const [categoryId, setCategoryId] = useState('')
    const [importance, setImportance] = useState<string>('Important')
    const [email, setEmail] = useState('')
    const [name, setName] = useState('')
    const [subscribeToUpdates, setSubscribeToUpdates] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [submitted, setSubmitted] = useState(false)
    const [submittedMsg, setSubmittedMsg] = useState('')
    const [dragOver, setDragOver] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const upload = useImageUpload(token) // signed-in only

    const categories = products.find(p => p.id === productId)?.categories ?? []

    const mut = useMutation({
        mutationFn: () => apiPost('/api/roadmap/requests', token, {
            description,
            title: title.trim() || null,
            productId: productId || null,
            categoryId: categoryId || null,
            importance,
            email: isAuth ? null : email,
            submitterName: isAuth ? null : name.trim() || null,
            subscribeToUpdates,
            imageIds: isAuth ? upload.images.map(i => i.id) : undefined,
        }),
        onSuccess: (data: any) => {
            const msg = data?.message ?? 'Idea submitted!'
            if (isAuth) {
                onSubmitted(msg)
            } else {
                setSubmitted(true)
                setSubmittedMsg(msg)
            }
        },
        onError: (e: any) => {
            let msg = 'Failed to submit.'
            try { msg = JSON.parse(e?.message)?.error ?? e?.message ?? msg } catch { msg = e?.message ?? msg }
            setError(msg)
        },
    })

    if (submitted) {
        return (
            <div className="modal-backdrop">
                <div className="modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Idea submitted!</h2>
                        <button className="modal-close" onClick={onClose}>✕</button>
                    </div>
                    <div className="modal-body" style={{ textAlign: 'center', padding: 'var(--space-8) var(--space-6)' }}>
                        <div style={{ fontSize: 48, marginBottom: 'var(--space-4)' }}>📬</div>
                        <p style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
                            {submittedMsg}
                        </p>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-primary" onClick={onClose}>Close</button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="modal-backdrop">
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Submit a new idea</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div
                    className="modal-body"
                    onPaste={isAuth ? e => {
                        const fs = imageFilesFrom(e.clipboardData.items, e.clipboardData.files)
                        if (fs.length) { e.preventDefault(); upload.uploadFiles(fs) }
                    } : undefined}
                >
                    {!isAuth && (
                        <div className="pub-submit-auth-hint">
                            <span>Submitting as guest &mdash; you'll receive a confirmation email. </span>
                            <Link to="/login">Sign in</Link> for instant submission.
                        </div>
                    )}
                    <div className="form-field">
                        <label className="form-label">Title <span style={{ color: 'var(--color-error)' }}>*</span></label>
                        <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="A short summary" autoFocus />
                    </div>
                    <div className="form-field">
                        <label className="form-label">What would you like to be able to do? How would it help? <span style={{ color: 'var(--color-error)' }}>*</span></label>
                        <textarea
                            className="form-textarea"
                            rows={5}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Describe the idea…"
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                        <div className="form-field" style={{ flex: 1 }}>
                            <label className="form-label">Product (optional)</label>
                            <select className="form-select" value={productId} onChange={e => { setProductId(e.target.value); setCategoryId('') }}>
                                <option value="">— None —</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="form-field" style={{ flex: 1 }}>
                            <label className="form-label">Category (optional)</label>
                            <select className="form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)} disabled={!categories.length}>
                                <option value="">— None —</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                    {isAuth && (
                        <div className="form-field">
                            <label className="form-label">Images (optional)</label>
                            <div
                                className={`roadmap-dropzone${dragOver ? ' dragover' : ''}`}
                                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={e => {
                                    e.preventDefault(); setDragOver(false)
                                    const fs = imageFilesFrom(e.dataTransfer.items, e.dataTransfer.files)
                                    if (fs.length) upload.uploadFiles(fs)
                                }}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={e => { if (e.target.files) upload.uploadFiles(Array.from(e.target.files)); e.target.value = '' }}
                                />
                                <span>📎 Drag &amp; drop, paste, or <strong>browse</strong> to attach images</span>
                            </div>
                            <UploadThumbs images={upload.images} onRemove={upload.remove} uploading={upload.uploading} />
                            {upload.error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-xs)', marginTop: 4 }}>{upload.error}</div>}
                        </div>
                    )}
                    {!isAuth && (
                        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                            <div className="form-field" style={{ flex: 1 }}>
                                <label className="form-label">Your name (optional)</label>
                                <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" />
                            </div>
                            <div className="form-field" style={{ flex: 1 }}>
                                <label className="form-label">Email address <span style={{ color: 'var(--color-error)' }}>*</span></label>
                                <input
                                    className="form-input"
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                />
                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                                    A confirmation link will be sent to this address.
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="form-field">
                        <label className="form-label">How important is this to you?</label>
                        <div className="importance-picker">
                            {IMPORTANCE.map(i => (
                                <button
                                    key={i.value}
                                    type="button"
                                    className={`importance-btn${importance === i.value ? ' selected' : ''}`}
                                    onClick={() => setImportance(i.value)}
                                >
                                    {i.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <label className="roadmap-subscribe-row">
                        <input
                            type="checkbox"
                            checked={subscribeToUpdates}
                            onChange={e => setSubscribeToUpdates(e.target.checked)}
                        />
                        <span>
                            <strong>Keep me posted on progress.</strong>{' '}
                            Email me{!isAuth ? ' (at the address above)' : ''} when this idea moves to a new roadmap phase.
                        </span>
                    </label>
                    {error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>{error}</div>}
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button
                        className="btn btn-primary"
                        disabled={!title.trim() || !description.trim() || (!isAuth && !email.trim()) || mut.isPending}
                        onClick={() => mut.mutate()}
                    >
                        {mut.isPending ? 'Submitting…' : 'Submit idea'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Sent for cleared product/category/phase selects — the API maps Guid.Empty to "clear".
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

// SuperAdmin inline-management panel shown inside the detail modal (no need to leave the board).
function DetailAdminControls({ r, phases, products, token, onSaved }: {
    r: RequestDetail; phases: Phase[]; products: Product[]; token: string | null; onSaved: () => void
}) {
    const qc = useQueryClient()
    const [phaseId, setPhaseId] = useState(r.phaseId ?? '')
    const [productId, setProductId] = useState(r.productId ?? '')
    const [categoryId, setCategoryId] = useState(r.categoryId ?? '')
    const [status, setStatus] = useState((r as any).status ?? 'Published')
    const [targetRelease, setTargetRelease] = useState(r.targetRelease ?? '')
    const [headOfficeOnly, setHeadOfficeOnly] = useState(r.isHeadOfficeOnly ?? false)
    const categories = products.find(p => p.id === productId)?.categories ?? []

    const saveMut = useMutation({
        mutationFn: () => apiPut(`/api/admin/roadmap/requests/${r.id}`, token, {
            phaseId: phaseId || EMPTY_GUID,
            productId: productId || EMPTY_GUID,
            categoryId: categoryId || EMPTY_GUID,
            status,
            targetRelease,
            isHeadOfficeOnly: headOfficeOnly,
        }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ['public-roadmap-request', r.id] }); onSaved() },
    })

    return (
        <div className="roadmap-detail-section roadmap-admin-panel">
            <h4>🛠️ Admin controls</h4>
            <div className="roadmap-admin-grid">
                <label>Phase
                    <select className="form-select" value={phaseId} onChange={e => setPhaseId(e.target.value)}>
                        <option value="">— None —</option>
                        {phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
                <label>Status
                    <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
                        {['Pending', 'PendingConfirmation', 'Published', 'Declined', 'Archived'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <label>Product
                    <select className="form-select" value={productId} onChange={e => { setProductId(e.target.value); setCategoryId('') }}>
                        <option value="">— None —</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
                <label>Category
                    <select className="form-select" value={categoryId} onChange={e => setCategoryId(e.target.value)} disabled={!categories.length}>
                        <option value="">— None —</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </label>
                <label>Target release
                    <input className="form-input" value={targetRelease} onChange={e => setTargetRelease(e.target.value)} placeholder="e.g. Q3 2026" />
                </label>
                <label className="roadmap-admin-checkbox">
                    <input type="checkbox" checked={headOfficeOnly} onChange={e => setHeadOfficeOnly(e.target.checked)} />
                    HeadOffice only
                </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                <button className="btn btn-primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>
                    {saveMut.isPending ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </div>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  Detail modal
// ════════════════════════════════════════════════════════════════════════
function PubDetailModal({ id, phaseById, productById, categoryById, phases, products, isAuth, isSuperAdmin, token, onClose, onSaved, onVote, onUnvote }: {
    id: string
    phaseById: Map<string, Phase>; productById: Map<string, Product>; categoryById: Map<string, Category>
    phases: Phase[]; products: Product[]
    isAuth: boolean; isSuperAdmin: boolean; token: string | null
    onClose: () => void; onSaved: () => void; onVote: (importance: string) => void; onUnvote: () => void
}) {
    const qc = useQueryClient()
    const [comment, setComment] = useState('')
    const commentUpload = useImageUpload(token)

    const { data: r, isLoading } = useQuery<RequestDetail>({
        queryKey: ['public-roadmap-request', id],
        queryFn: () => api(`/api/roadmap/requests/${id}`, token),
    })

    const commentMut = useMutation({
        mutationFn: () => apiPost(`/api/roadmap/requests/${id}/comments`, token, {
            body: comment,
            imageIds: commentUpload.images.map(i => i.id),
        }),
        onSuccess: () => {
            setComment(''); commentUpload.setImages([])
            qc.invalidateQueries({ queryKey: ['public-roadmap-request', id] })
        },
    })

    const phase = r?.phaseId ? phaseById.get(r.phaseId) : undefined
    const product = r?.productId ? productById.get(r.productId) : undefined
    const category = r?.categoryId ? categoryById.get(r.categoryId) : undefined

    return (
        <div className="modal-backdrop">
            <div className="modal roadmap-detail" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isLoading ? 'Loading…' : r?.title}</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                {r && (
                    <div className="modal-body">
                        <div className="roadmap-card-meta" style={{ marginTop: 0 }}>
                            {product && <span className="roadmap-chip" style={{ background: (product.color ?? '#888') + '22', color: product.color ?? undefined }}>{product.icon} {product.name}</span>}
                            {category && <span className="roadmap-chip">{category.name}</span>}
                            {phase && <span className="roadmap-chip phase" style={{ background: phase.color ?? 'var(--color-primary)' }}>{phase.icon} {phase.name}</span>}
                            {r.targetRelease && <span className="roadmap-chip">🎯 {r.targetRelease}</span>}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                            Submitted by <strong style={{ color: 'var(--color-text-secondary)' }}>{r.submitter ?? 'Anonymous'}</strong> · {fmtDate(r.createdAt)}
                        </div>

                        {isSuperAdmin && (
                            <DetailAdminControls r={r} phases={phases} products={products} token={token} onSaved={onSaved} />
                        )}

                        <div className="roadmap-detail-section">
                            <h4>Idea</h4>
                            <p>{r.description}</p>
                        </div>
                        {r.problem && <div className="roadmap-detail-section"><h4>🚩 Problem</h4><p>{r.problem}</p></div>}
                        {r.plannedSolution && <div className="roadmap-detail-section"><h4>📋 Planned solution</h4><p>{r.plannedSolution}</p></div>}
                        {r.imageUrl && <div className="roadmap-detail-section"><img className="roadmap-detail-image" src={r.imageUrl} alt="" /></div>}
                        {r.images && r.images.length > 0 && (
                            <div className="roadmap-detail-section">
                                <h4>🖼️ Images</h4>
                                <RoadmapGallery images={r.images} />
                            </div>
                        )}

                        <div className="roadmap-detail-section">
                            <h4>How important is this to you? ({r.voteCount} {r.voteCount === 1 ? 'vote' : 'votes'}, score {r.voteScore})</h4>
                            {isAuth ? (
                                <>
                                    <div className="importance-picker">
                                        {IMPORTANCE.map(i => (
                                            <button
                                                key={i.value}
                                                className={`importance-btn${r.myVote === i.weight ? ' selected' : ''}`}
                                                onClick={() => onVote(i.value)}
                                            >
                                                {i.label}
                                            </button>
                                        ))}
                                    </div>
                                    {r.myVote ? (
                                        <button className="vote-remove" style={{ marginTop: 'var(--space-2)' }} onClick={onUnvote}>
                                            Remove my vote ({weightToLabel(r.myVote)})
                                        </button>
                                    ) : null}
                                </>
                            ) : (
                                <div className="pub-vote-signin">
                                    <Link to="/login" className="btn btn-primary">Sign in to vote</Link>
                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>
                                        — voting helps us prioritise the roadmap.
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="roadmap-detail-section">
                            <h4>Discussion ({r.comments.length})</h4>
                            {r.comments.map(c => (
                                <div className="comment" key={c.id}>
                                    <div className="comment-head">
                                        <span className="comment-author">{c.author ?? 'Anonymous'}</span>
                                        {c.isOfficial && <span className="comment-official">Official</span>}
                                        <span className="comment-time">{fmtDate(c.createdAt)}</span>
                                    </div>
                                    {c.body && <div className="comment-body">{c.body}</div>}
                                    <RoadmapGallery images={c.images} />
                                </div>
                            ))}
                            {isAuth ? (
                                <div style={{ marginTop: 'var(--space-3)' }}>
                                    <textarea
                                        className="form-textarea"
                                        rows={2}
                                        placeholder="Add a comment… (paste an image to attach it)"
                                        value={comment}
                                        onChange={e => setComment(e.target.value)}
                                        onPaste={e => {
                                            const fs = imageFilesFrom(e.clipboardData.items, e.clipboardData.files)
                                            if (fs.length) { e.preventDefault(); commentUpload.uploadFiles(fs) }
                                        }}
                                    />
                                    <UploadThumbs images={commentUpload.images} onRemove={commentUpload.remove} uploading={commentUpload.uploading} />
                                    {commentUpload.error && <div style={{ color: 'var(--color-error)', fontSize: 'var(--text-xs)' }}>{commentUpload.error}</div>}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                                        <button
                                            className="btn btn-primary"
                                            disabled={(!comment.trim() && commentUpload.images.length === 0) || commentMut.isPending}
                                            onClick={() => commentMut.mutate()}
                                        >
                                            {commentMut.isPending ? 'Posting…' : 'Post comment'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="pub-vote-signin" style={{ marginTop: 'var(--space-3)' }}>
                                    <Link to="/login" className="btn btn-secondary">Sign in to comment</Link>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
